import { useTranslation } from 'react-i18next';
import { enCA, frCA } from 'date-fns/locale';
import type { Locale } from 'date-fns';

/**
 * The date-fns locale each language renders dates in, keyed by the base subtag so a region variant
 * resolves with its language — `fr`, `fr-CA` and `fr-FR` all land on `frCA`, matching how the apps
 * register one bundle per language rather than per region. Adding a language is an entry here
 * rather than another branch at every call site.
 */
const DATE_LOCALES: Record<string, Locale> = {
  en: enCA,
  fr: frCA,
};

/** What an unmapped language renders as. */
const FALLBACK_LOCALE = enCA;

/**
 * The date-fns locale to pass to `format()`'s `locale` option, so month and weekday names render in
 * the language the user is reading — date-fns falls back to US English otherwise.
 *
 * ```tsx
 * const dateLocale = useDateLocale();
 * format(date, 'PP', { locale: dateLocale });
 * ```
 *
 * Lives here rather than in one app because every portal formats dates and each had been deciding
 * the mapping inline, which is both duplicated and easy to get subtly wrong: an exact-match check
 * (`language === 'fr'`) silently renders `fr-CA` in English. A language i18next knows but this map
 * does not renders in English rather than throwing.
 *
 * Reads the language from whichever i18next instance the host app initialised: vite dedupes
 * i18next/react-i18next to a single copy, so this resolves against the app's own instance the same
 * way the library's translated components do.
 */
export default function useDateLocale(): Locale {
  const { i18n } = useTranslation();
  const [baseSubtag] = (i18n.language || '').toLowerCase().split('-');

  return DATE_LOCALES[baseSubtag] ?? FALLBACK_LOCALE;
}
